import React, { useEffect, useMemo, useState } from 'react';
import { Trophy, Zap, Flame, AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { AchievementUnlock } from '../lib/gamification';
import type { Language, Translations } from '@/lib/translations';

interface RewardSummaryEventDetail {
  type: 'reward-summary';
  xp: number;
  totalXp: number;
  level: number;
  progress: number;
  streak: number;
  achievements: AchievementUnlock[];
}

interface RewardErrorEventDetail {
  type: 'reward-error';
  message: string;
}

interface StreakWarningEventDetail {
  type: 'streak-warning';
  currentStreak: number;
}

interface ComeBackEventDetail {
  type: 'come-back';
  xpEarned: number;
}

type RewardToastDetail = RewardSummaryEventDetail | RewardErrorEventDetail | StreakWarningEventDetail | ComeBackEventDetail;

type QueuedToast = (RewardToastDetail & {
  id: string;
  createdAt: number;
});


const SummaryToastCard: React.FC<{ entry: RewardSummaryEventDetail; t: Translations; language: Language }> = ({ entry, t, language }) => {
  const safeXp = entry.xp ?? 0;
  const progressPct = Math.round((entry.progress ?? 0) * 100);
  const xpLabel = safeXp === 1
    ? t.xpLabelSingular
    : t.xpLabelPlural.replace('{count}', safeXp.toLocaleString());
  const achievementText = useMemo(() => {
    if (!entry.achievements.length) return null;
    const separator = language === 'ar' ? '، ' : ', ';
    return entry.achievements
      .map((item) => item.title)
      .join(separator);
  }, [entry.achievements, language]);

  const levelStreak = t.rewardsToastLevelStreak
    .replace('{level}', (entry.level ?? 1).toString())
    .replace('{streak}', (entry.streak ?? 1).toLocaleString())
    .replace('{days}', t.daysLabel);

  const totalXp = t.rewardsToastTotalXp.replace('{total}', (entry.totalXp ?? 0).toLocaleString());

  return (
    <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border border-dashed border-amber-200 bg-white/95 shadow-xl backdrop-blur px-5 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="text-amber-500" size={20} />
          <p className="text-sm font-semibold text-amber-700">{t.rewardsToastTitle}</p>
        </div>
        <span className="text-xs font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
          {xpLabel}
        </span>
      </div>
      <div className="mt-3 space-y-2 text-sm text-slate-700">
        <p className="font-medium">{levelStreak}</p>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Zap size={14} className="text-blue-500" />
          <span>{totalXp}</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-amber-100">
          <div
            className="h-full bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"
            style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
          />
        </div>
        {achievementText && (
          <div className="flex items-start gap-2 text-xs text-purple-600">
            <Flame size={14} className="mt-0.5" />
            <span>{achievementText}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ErrorToastCard: React.FC<{ entry: RewardErrorEventDetail; t: Translations }> = ({ entry, t }) => {
  return (
    <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border border-dashed border-red-200 bg-white shadow-xl px-5 py-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 text-red-500" size={20} />
        <div>
          <p className="text-sm font-semibold text-red-600">{t.rewardsToastErrorTitle}</p>
          <p className="mt-1 text-xs text-red-500 leading-relaxed">{entry.message}</p>
        </div>
      </div>
    </div>
  );
};

const StreakWarningToastCard: React.FC<{ entry: StreakWarningEventDetail; language: Language }> = ({ entry, language }) => {
  const title = language === 'ar' ? '🔥 لا تفقد سلسلتك!' : '🔥 Don\'t break your streak!';
  const message = language === 'ar'
    ? `لديك سلسلة ${entry.currentStreak} يوم! ادرس اليوم للحفاظ عليها.`
    : `You have a ${entry.currentStreak}-day streak! Study today to keep it alive.`;

  return (
    <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border border-dashed border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-xl px-5 py-4 animate-pulse">
      <div className="flex items-start gap-3">
        <Flame className="mt-0.5 text-orange-500 animate-bounce" size={24} />
        <div>
          <p className="text-sm font-bold text-orange-700">{title}</p>
          <p className="mt-1 text-xs text-orange-600 leading-relaxed">{message}</p>
        </div>
      </div>
    </div>
  );
};

const ComeBackToastCard: React.FC<{ entry: ComeBackEventDetail; language: Language }> = ({ entry, language }) => {
  const title = language === 'ar' ? '🎉 عمل رائع!' : '🎉 Great work!';
  const message = language === 'ar'
    ? `لقد كسبت ${entry.xpEarned} XP! عد غداً لمواصلة التقدم.`
    : `You earned ${entry.xpEarned} XP! Come back tomorrow to keep progressing.`;

  return (
    <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border border-dashed border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50 shadow-xl px-5 py-4">
      <div className="flex items-start gap-3">
        <Trophy className="mt-0.5 text-emerald-500" size={24} />
        <div>
          <p className="text-sm font-bold text-emerald-700">{title}</p>
          <p className="mt-1 text-xs text-emerald-600 leading-relaxed">{message}</p>
        </div>
      </div>
    </div>
  );
};


const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

export const RewardToastHub: React.FC = () => {
  const [queue, setQueue] = useState<QueuedToast[]>([]);
  const { t, language } = useLanguage();

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<RewardToastDetail>).detail;
      if (!detail) return;
      const validTypes = ['reward-summary', 'reward-error', 'streak-warning', 'come-back'];
      if (!validTypes.includes(detail.type)) return;
      setQueue((prev) => [
        ...prev,
        {
          ...detail,
          id: createId(),
          createdAt: Date.now(),
        },
      ]);
    };
    window.addEventListener('msd-toast', handler as EventListener);
    return () => window.removeEventListener('msd-toast', handler as EventListener);
  }, []);


  useEffect(() => {
    if (!queue.length) return;
    const timeout = setTimeout(() => {
      setQueue((prev) => prev.slice(1));
    }, 6000);
    return () => clearTimeout(timeout);
  }, [queue]);

  if (!queue.length) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-3 px-4">
      {queue.map((entry) => {
        switch (entry.type) {
          case 'reward-summary':
            return <SummaryToastCard key={entry.id} entry={entry as RewardSummaryEventDetail} t={t} language={language} />;
          case 'reward-error':
            return <ErrorToastCard key={entry.id} entry={entry as RewardErrorEventDetail} t={t} />;
          case 'streak-warning':
            return <StreakWarningToastCard key={entry.id} entry={entry as StreakWarningEventDetail} language={language} />;
          case 'come-back':
            return <ComeBackToastCard key={entry.id} entry={entry as ComeBackEventDetail} language={language} />;
          default:
            return null;
        }
      })}

    </div>
  );
};

export default RewardToastHub;
